import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

export interface IconWrapperProps extends ThemeOverrideProps {
  /** Size of the wrapper */
  size?: "xl" | "lg" | "md" | "sm" | "xs" | "xxs";
  /** Shape of the wrapper */
  shape?: "none" | "full" | "smooth";
  /** Type of content being wrapped. Affects default styling. */
  type?: "icon" | "label" | "image" | "avatar" | "brand" | "custom";
  /** Children to be rendered inside the wrapper */
  children?: React.ReactNode;
  /** Background color override */
  backgroundColor?: string;
  /** Border color override */
  borderColor?: string;
  testID?: string;
}

/**
 * A universal container component used to display small visual elements in a consistent and scalable way.
 * It ensures uniform sizing, alignment, and shapes across different types of content.
 */
export const IconWrapper: React.FC<IconWrapperProps> = ({
  size = "md",
  shape = "none",
  children,
  backgroundColor,
  borderColor,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const sizeConfig = theme.sizing.iconWrapper(size);

  let borderRadius = 0;
  if (shape === "full") {
    borderRadius = 999;
  } else if (shape === "smooth") {
    // "Smooth" is slightly rounded. We use a relative radius based on size.
    borderRadius = Math.max(4, Math.floor(sizeConfig.size / 6));
  }

  const bg =
    backgroundColor ||
    (shape === "none" ? "transparent" : theme.colors.background.secondary);

  return (
    <Box
      testID={testID}
      width={sizeConfig.size}
      height={sizeConfig.size}
      borderRadius={borderRadius}
      backgroundColor={bg}
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      borderColor={borderColor}
      borderWidth={borderColor ? 1 : 0}
      position="relative"
    >
      {children}
    </Box>
  );
};
